<script>
import { getUpcomingDispatches, getUpcomingDispatchFilters } from '@/api/cars'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import MultiSelectHint from '@/components/ui/MultiSelectHint.vue'
import Checkbox from '@/components/ui/inputs/Checkbox.vue'
import Notice from '@/components/cars/edit/Notice.vue'
import groupBy from 'lodash/groupBy'
import toPairs from 'lodash/toPairs'
import orderBy from 'lodash/orderBy'
import fromPairs from 'lodash/fromPairs'
import { DateTime } from 'luxon'
import { isEmpty, nl2br } from '@/helpers/utils'
import { currency } from '@/filters/currency'
import TerminatedPotentials from '@/components/cars/TerminatedPotentials.vue'

export default {
  components: {
    'mia-checkbox': Checkbox,
    'mia-feature-input': FeatureInput,
    'mia-feature-select': FeatureSelect,
    'mia-multi-select-hint': MultiSelectHint,
    'mia-notice': Notice,
    'mia-terminated-potentials': TerminatedPotentials
  },

  filters: {
    currency
  },

  data: () => ({
    activeTab: 'upcomingDispatches',
    dispatchAssignees: [],
    sellers: [],
    salesChannels: [],
    messages: [],
    cars: [],
    carsWithoutDispatchDate: {},
    filters: {
      dispatchFrom: '',
      dispatchTo: '',
      dispatchAssignees: [],
      dispatchAssigneeSelf: false,
      seller: '',
      salesChannel: ''
    }
  }),

  computed: {
    currentCalendarWeek() {
      const date = DateTime.local()

      return `${date.year}-${date.weekNumber}`
    },

    carsByCalendarWeek() {
      const cars = groupBy(this.cars, 'item_calendar_week')

      if (cars[this.currentCalendarWeek] === undefined) {
        cars[this.currentCalendarWeek] = []
      }

      return fromPairs(orderBy(toPairs(cars), ([calendarWeek]) => {
        const [year, weekNumber] = calendarWeek.split('-')

        return DateTime.local().set({ year: parseInt(year, 10), weekNumber: parseInt(weekNumber, 10) })
      }, ['asc', 'asc']))
    },

    presentCalendarWeeks() {
      return Object.keys(this.carsByCalendarWeek)
    },

    hasCarsWithoutDispatchDate() {
      return Object.keys(this.carsWithoutDispatchDate).length > 0
    },

    orderedCarsWithoutDispatchDate() {
      return fromPairs(orderBy(toPairs(this.carsWithoutDispatchDate), ([calendarWeek]) => {
        const [year, month] = calendarWeek.split('-')

        return DateTime.local().set({ year: parseInt(year, 10), month: parseInt(month, 10) })
      }, ['asc', 'asc']))
    }
  },

  watch: {
    filters: {
      deep: true,
      handler() {
        this.loadUpcomingDispatches()
      }
    }
  },

  created() {
    this.loadUpcomingDispatchFilters()
    this.loadUpcomingDispatches()
  },

  mounted() {
    window.eventBus.$emit('mia-loading-ended')
    this.setBreadcrumbs()
  },

  methods: {
    nl2br,

    setBreadcrumbs(amount = '') {
      window.eventBus.$emit('mia-set-breadcrumb', [
        {
          link: {
            path: '/cars'
          },
          text: 'Fahrzeuge'
        },
        {
          link: {
            path: '/cars/upcoming-dispatch'
          },
          text: `Anstehende Auslieferungen (${amount})`.replace(/\(\)/, '').trim()
        }
      ])
    },

    async loadUpcomingDispatchFilters() {
      this.$wait.start('loading upcoming dispatch filters')
      const [error, response] = await getUpcomingDispatchFilters()
      this.$wait.end('loading upcoming dispatch filters')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Die Filter konnten nicht geladen werden']
        })
        return
      }

      this.dispatchAssignees = response.dispatchAssignees
      this.sellers = response.sellers
      this.salesChannels = response.salesChannels
    },

    async loadUpcomingDispatches() {
      this.$wait.start('loading upcoming dispatches')
      const [error, response] = await getUpcomingDispatches({
        item_dispatch_from: this.filters.dispatchFrom || null,
        item_dispatch_till: this.filters.dispatchTo || null,
        item_dispatch_assignees: this.filters.dispatchAssigneeSelf ? null : this.filters.dispatchAssignees,
        item_dispatch_assignee_self: this.filters.dispatchAssigneeSelf ? 'Y' : 'N',
        item_seller: this.filters.seller || null,
        item_saleschannel: this.filters.salesChannel || null
      })
      this.$wait.end('loading upcoming dispatches')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Die anstehenden Auslieferungen konnten nicht geladen werden']
        })
        return
      }

      this.messages = response.messages
      this.cars = response.cars
      this.carsWithoutDispatchDate = response.carsWithoutDispatchDate

      this.setBreadcrumbs(this.cars.length)
    },

    hasEntriesBeforeCalendarWeek(givenCalendarWeek) {
      const [givenYear, givenWeekNumber] = givenCalendarWeek.split('-')

      return this.presentCalendarWeeks.some((calendarWeek) => {
        const [year, weekNumber] = calendarWeek.split('-')

        if (parseInt(year, 10) > parseInt(givenYear, 10)) {
          return false
        }

        return parseInt(weekNumber, 10) <= parseInt(givenWeekNumber, 10)
      })
    },

    formatCalendarWeek(calendarWeek) {
      const [year, week] = calendarWeek.split('-')

      return `KW ${week}/${year}`
    },

    timestampToFormattedDateTime(timestamp) {
      return `${DateTime.fromSeconds(parseInt(timestamp, 10)).toFormat('EEEE, dd.MM.yyyy, HH:mm')} Uhr`
    },

    sqlDateToFormattedDate(date, outputFormat = 'EEEE, dd.MM.yyyy') {
      if (isEmpty(date)) {
        return ''
      }

      return `${DateTime.fromSQL(date).toFormat(outputFormat)}`
    },

    formatYearAndMonth(yearAndMonth) {
      const [year, month] = yearAndMonth.split('-')

      return DateTime.local().set({ year, month }).toFormat('MMMM yyyy')
    },

    getRegistrationInstanceLabel(registrationInstance) {
      return {
        customer: 'Kunde',
        dealer: 'Autohaus',
        registration_service: 'Zulassungsdienst',
        mediator: 'Vermittler'
      }[registrationInstance] || registrationInstance
    }
  }
}
</script>

<template>
  <div class="w-full">
    <router-link
      to="/legacy/upcoming_dispatch?force_legacy=1"
      class="text-orange-mia hover:text-orange-mia-dark hover:underline block mb-4"
    >
      Zum alten Anstehende Auslieferungen UI »
    </router-link>

    <div
      v-if="[1, 2].includes(parseInt(authDealer.id, 10))"
      class="border-b-2 border-gray-400"
    >
      <button
        class="px-6 py-3 transition-colors duration-100 border-b-2 border-transparent tab focus:outline-none hover:border-orange-mia"
        :class="{
          'border-orange-mia': activeTab === 'upcomingReregistrations',
        }"
        style="margin-bottom: -2px"
        @click="activeTab = 'upcomingDispatches'"
      >
        Anstehende Ummeldungen
      </button>
      <button
        class="px-6 py-3 transition-colors duration-100 border-b-2 border-transparent tab focus:outline-none hover:border-orange-mia"
        :class="{
          'border-orange-mia': activeTab === 'upcomingDispatches',
        }"
        style="margin-bottom: -2px"
        @click="activeTab = 'upcomingDispatches'"
      >
        Anstehende Auslieferungen ({{ cars.length }})
      </button>
      <button
        class="px-6 py-3 transition-colors duration-100 border-b-2 border-transparent tab focus:outline-none hover:border-orange-mia"
        :class="{
          'border-orange-mia': activeTab === 'terminatedPotentials',
        }"
        style="margin-bottom: -2px"
        @click="activeTab = 'terminatedPotentials'"
      >
        Terminierte Potentiale
      </button>
    </div>

    <div class="mt-2">
      <div v-show="activeTab === 'upcomingReregistrations'">
        <mia-wait
          for="loading upcoming dispatch filters"
          class="mt-4"
        >
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <template #default>
            <div class="grid grid-cols-5 gap-6 p-4 -mx-2 bg-gray-200 items-start">
              <mia-feature-input
                v-model="filters.dispatchFrom"
                label="Terminierte Ummeldung von"
                type="date"
              />
              <mia-feature-input
                v-model="filters.dispatchTo"
                label="Terminierte Ummeldung bis"
                type="date"
              />
              <div
                class="relative flex flex-col justify-end"
              >
                <label
                  for="dispatchAssigneeSelect"
                  class="mb-1 mr-2 tracking-wider text-gray-800"
                >
                  Zuständig für die Auslieferung
                </label>

                <select
                  id="dispatchAssigneeSelect"
                  v-model="filters.dispatchAssignees"
                  class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
                  :disabled="filters.dispatchAssigneeSelf"
                  multiple
                >
                  <option
                    v-for="dispatchAssignee in dispatchAssignees"
                    :key="dispatchAssignee.id"
                    class="text-black"
                    :value="dispatchAssignee.id"
                  >
                    {{ dispatchAssignee.item_full_name }}
                  </option>
                </select>
                <mia-multi-select-hint class="mt-1" />
                <label
                  class="font-normal flex items-center text-sm mt-2 cursor-pointer"
                  style="margin-bottom: 0"
                >
                  <mia-checkbox
                    v-model="filters.dispatchAssigneeSelf"
                    :checked="filters.dispatchAssigneeSelf"
                    class="w-5 h-5"
                    @input="$set(filters, 'dispatchAssigneeSelf', $event.target.checked)"
                  />
                  <span class="ml-2 block mt-px">Nur meine Auslieferungen anzeigen</span>
                </label>
              </div>
              <mia-feature-select
                v-model="filters.seller"
                label="Verkäufer"
                :options="sellers"
                :name-by="(seller) => `${seller.item_lastname}, ${seller.item_firstname}`"
                track-by="id"
              />
              <mia-feature-select
                v-model="filters.salesChannel"
                label="Vertriebskanal"
                :options="salesChannels"
                name-by="item_title"
                track-by="id"
              />
            </div>
          </template>
        </mia-wait>

        <mia-wait
          for="loading upcoming dispatches"
          class="mt-6"
        >
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <template #default>
            <div
              v-if="messages.length > 0"
              class="mt-6"
            >
              <mia-notice permanent>
                <div class="space-y-4">
                  <p
                    v-for="message in messages"
                    :key="message"
                    style="margin: 0"
                  >
                    {{ message }}
                  </p>
                </div>
              </mia-notice>
            </div>

            <div class="mt-6">
              <div class="flex flex-col">
                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-sm">
                      <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-gray-400">
                          <tr>
                            <th
                              scope="col"
                              class="py-2 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                            >
                              Termin
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Terminierte Zulassung
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Zulassung durch
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Übergabeinspektion
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Aufbereitung
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Fahrzeug
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Verkäufer
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Kunde
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Brieflagerplatz
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              OPOS
                            </th>
                            <th
                              scope="col"
                              class="relative py-2 pl-3 pr-4"
                            >
                              Anmerkungen
                            </th>
                          </tr>
                        </thead>
                        <tbody class="bg-white">
                          <template v-if="cars.length === 0">
                            <tr>
                              <td
                                colspan="12"
                                class="whitespace-nowrap p-4 text-sm font-medium text-gray-900 text-center"
                              >
                                Keine anstehenden Auslieferungen gefunden.
                              </td>
                            </tr>
                          </template>
                          <template v-else>
                            <template v-for="(carsInCalendarWeek, calendarWeek) in carsByCalendarWeek">
                              <template v-if="calendarWeek === currentCalendarWeek">
                                <tr
                                  v-if="hasEntriesBeforeCalendarWeek(calendarWeek)"
                                  :key="`${calendarWeek}-before-indicator`"
                                  class="bg-gray-400"
                                >
                                  <td
                                    colspan="12"
                                    class="p-3"
                                  >
                                    <div class="text-gray-900 flex flex-col justify-center items-center">
                                      <div class="flex pb-2">
                                        <svg
                                          class="mr-1"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        >
                                          <polyline points="14 9 9 4 4 9" />
                                          <path d="M20 20h-7a4 4 0 0 1-4-4V4" />
                                        </svg>
                                        <span>verstrichene Termine</span>
                                      </div>
                                      <div
                                        class="border-t border-orange-mia mx-2 w-full"
                                        style="max-width: 40%;"
                                      />
                                      <div class="flex pt-2">
                                        <span>anstehende Termine</span>
                                        <svg
                                          class="ml-1 mt-px"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        >
                                          <polyline points="10 15 15 20 20 15" />
                                          <path d="M4 4h7a4 4 0 0 1 4 4v12" />
                                        </svg>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              </template>
                              <template v-if="carsInCalendarWeek.length > 0">
                                <tr
                                  :key="calendarWeek"
                                  class="bg-gray-300"
                                >
                                  <td
                                    colspan="12"
                                    class="py-5 px-4"
                                  >
                                    <h2
                                      style="margin: 0; padding: 0"
                                      :class="{
                                        'font-bold flex items-center': calendarWeek === currentCalendarWeek
                                      }"
                                    >
                                      {{ formatCalendarWeek(calendarWeek) }}<span
                                        v-if="calendarWeek === currentCalendarWeek"
                                        class="font-normal ml-2 text-base tracking-wide"
                                      >
                                        - Diese Woche
                                      </span>
                                    </h2>
                                  </td>
                                </tr>
                                <template v-for="(car, index) in carsInCalendarWeek">
                                  <tr
                                    :key="`${car.id}-car`"
                                    class="hover:bg-blue-100"
                                    :class="{
                                      'bg-gray-100': index % 2 === 0
                                    }"
                                  >
                                    <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 align-top">
                                      {{ timestampToFormattedDateTime(car.item_dispatch) }}
                                      <div
                                        v-if="car.item_terminated_ak_date"
                                        class="text-sm mt-2 text-red-600"
                                      >
                                        Terminierte AK Meldung am <b>{{ sqlDateToFormattedDate(car.item_terminated_ak_date, 'dd.MM.yyyy') }}</b>
                                      </div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ sqlDateToFormattedDate(car.item_registration_date) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ getRegistrationInstanceLabel(car.item_registration_instance) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ sqlDateToFormattedDate(car.item_handover_inspection_date) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ sqlDateToFormattedDate(car.item_preparation_date) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <div
                                        :style="{
                                          color: `#${car.item_status_color}`
                                        }"
                                      >
                                        {{ car.item_status_title }}
                                      </div>
                                      <div>{{ car.item_type }}</div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <div>
                                        <router-link
                                          :to="`/cars/${car.id}`"
                                          class="hover:underline"
                                        >
                                          {{ car.item_fin || 'Ohne FIN' }}
                                        </router-link>
                                      </div>
                                      <div>
                                        {{ car.item_model_title }}
                                      </div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <router-link
                                        :to="`/users/profile/${car.item_sold_seller}`"
                                        class="hover:underline"
                                      >
                                        {{ car.item_sold_seller_name }}
                                      </router-link>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <router-link
                                        :to="`/customers/${car.item_customer}`"
                                        class="hover:underline flex items-center"
                                      >
                                        <mia-streamline-icon
                                          :src="car.item_customer_company && car.item_customer_company.trim().length > 0 ? 'building-1' : 'single-neutral'"
                                          class="w-4 h-4 mr-1"
                                        />
                                        <span>{{ car.item_customer_company && car.item_customer_company.trim().length > 0 ? car.item_customer_company : car.item_customer_name }}</span>
                                      </router-link>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <router-link
                                        v-if="car.item_certificate_slot"
                                        :to="`/legacy/certificate_edit/${car.item_certificate_slot}`"
                                        class="hover:underline"
                                      >
                                        {{ car.item_certificate_slot_title }}
                                      </router-link>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <span
                                        :class="{
                                          'text-red-600': car.item_opos_difference > 0,
                                          'text-green-600': car.item_opos_difference <= 0
                                        }"
                                      >
                                        {{ car.item_opos_difference > 0 ? '-' : '' }}{{ car.item_opos_difference | currency }}
                                      </span>
                                      <div v-if="car.item_invoicedate_sold && car.item_invoicedate_sold !== '0000-00-00'">
                                        <span class="text-xs">Re.-Datum:</span> {{ sqlDateToFormattedDate(car.item_invoicedate_sold, 'dd.MM.yyyy') }}
                                      </div>
                                      <div
                                        v-if="car.item_opos_finished_at"
                                        class="text-sm mt-2 text-green-700"
                                      >
                                        OPOS abgeschlossen
                                      </div>
                                      <div
                                        v-else
                                        class="text-sm mt-2 text-red-700"
                                      >
                                        OPOS noch nicht abgeschlossen
                                      </div>
                                    </td>
                                    <td class="relative whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium align-top">
                                      <!-- eslint-disable vue/no-v-html -->
                                      <div
                                        v-if="car.item_dispatch_notes"
                                        v-html="nl2br(car.item_dispatch_notes)"
                                      />
                                      <!-- eslint-enable vue/no-v-html -->

                                      <mia-is-allowed
                                        to="customer_portal"
                                        who="dealer"
                                      >
                                        <div
                                          :class="{
                                            'mt-2': !!car.item_dispatch_notes
                                          }"
                                          class="space-y-2"
                                        >
                                          <div
                                            v-if="car.item_dispatch_source === 'customer'"
                                            class="text-green-600 font-bold"
                                          >
                                            Terminbuchung durch Kunde
                                          </div>
                                          <div
                                            v-if="car.item_registration_package === 'customer'"
                                            :class="{
                                              'text-red-600': !car.item_registration_certificate_uploaded_at,
                                              'text-green-600': !!car.item_registration_certificate_uploaded_at
                                            }"
                                          >
                                            Zulassungsbescheinigung Teil II {{ car.item_registration_certificate_uploaded_at ? 'hochgeladen' : 'nicht hochgeladen' }}
                                          </div>
                                        </div>
                                      </mia-is-allowed>
                                    </td>
                                  </tr>
                                  <tr
                                    :key="`${car.id}-exchange`"
                                    class="hover:bg-blue-100"
                                    :class="{
                                      'bg-gray-100': index % 2 === 0
                                    }"
                                  >
                                    <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900">
                                      <mia-streamline-icon
                                        v-tooltip="'Inzahlungnahme'"
                                        src="arrow-button-up"
                                        class="w-5 h-5"
                                        title="Inzahlungnahme"
                                      />
                                    </td>
                                    <td
                                      colspan="4"
                                      class="whitespace-nowrap px-3 py-3 text-sm text-gray-900"
                                    />
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 font-bold">
                                      Inzahlungnahme
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                                      <router-link
                                        :to="`/cars/${car.item_exchange_id}`"
                                        class="hover:underline"
                                      >
                                        {{ car.item_exchange_fin || 'Ohne FIN' }}
                                      </router-link>
                                    </td>
                                    <td
                                      colspan="4"
                                      class="whitespace-nowrap px-3 py-3 text-sm text-gray-900"
                                    />
                                    <td class="relative whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium" />
                                  </tr>
                                </template>
                              </template>
                            </template>
                          </template>
                          <template v-if="hasCarsWithoutDispatchDate">
                            <template v-for="(carsInYearAndMonth, yearAndMonth) in orderedCarsWithoutDispatchDate">
                              <tr
                                :key="`${yearAndMonth}-indicator`"
                                class="bg-gray-400"
                              >
                                <td
                                  colspan="12"
                                  class="p-3"
                                >
                                  <div class="text-gray-900 flex justify-center items-center">
                                    <div
                                      class="border-t border-orange-mia mx-2 w-full"
                                      style="max-width: 20%;"
                                    />
                                    <div>
                                      {{ carsInYearAndMonth.length }} Fahrzeug{{ carsInYearAndMonth.length === 1 ? '' : 'e' }} im Monat {{ formatYearAndMonth(yearAndMonth) }} ohne Auslieferungstermin
                                    </div>
                                    <div
                                      class="border-t border-orange-mia mx-2 w-full"
                                      style="max-width: 20%;"
                                    />
                                  </div>
                                </td>
                              </tr>
                              <tr
                                v-for="(car, index) in carsInYearAndMonth"
                                :key="`${car.id}-car-without-dispatch-date`"
                                class="hover:bg-blue-100"
                                :class="{
                                  'bg-gray-100': index % 2 === 0
                                }"
                              >
                                <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 align-top" />
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ sqlDateToFormattedDate(car.item_registration_date) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ getRegistrationInstanceLabel(car.item_registration_instance) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ sqlDateToFormattedDate(car.item_handover_inspection_date) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ sqlDateToFormattedDate(car.item_preparation_date) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <div
                                    :style="{
                                      color: `#${car.item_status_color}`
                                    }"
                                  >
                                    {{ car.item_status_title }}
                                  </div>
                                  <div>{{ car.item_type }}</div>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <div>
                                    <router-link
                                      :to="`/cars/${car.id}`"
                                      class="hover:underline"
                                    >
                                      {{ car.item_fin || 'Ohne FIN' }}
                                    </router-link>
                                  </div>
                                  <div>
                                    {{ car.item_model_title }}
                                  </div>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <router-link
                                    :to="`/users/profile/${car.item_sold_seller}`"
                                    class="hover:underline"
                                  >
                                    {{ car.item_sold_seller_name }}
                                  </router-link>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <router-link
                                    :to="`/customers/${car.item_customer}`"
                                    class="hover:underline flex items-center"
                                  >
                                    <mia-streamline-icon
                                      :src="car.item_customer_company && car.item_customer_company.trim().length > 0 ? 'building-1' : 'single-neutral'"
                                      class="w-4 h-4 mr-1"
                                    />
                                    <span>{{ car.item_customer_company && car.item_customer_company.trim().length > 0 ? car.item_customer_company : car.item_customer_name }}</span>
                                  </router-link>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <router-link
                                    v-if="car.item_certificate_slot"
                                    :to="`/legacy/certificate_edit/${car.item_certificate_slot}`"
                                    class="hover:underline"
                                  >
                                    {{ car.item_certificate_slot_title }}
                                  </router-link>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <span
                                    :class="{
                                      'text-red-600': car.item_opos_difference > 0,
                                      'text-green-600': car.item_opos_difference <= 0
                                    }"
                                  >
                                    {{ car.item_opos_difference > 0 ? '-' : '' }}{{ car.item_opos_difference | currency }}
                                  </span>
                                  <div v-if="car.item_invoicedate_sold && car.item_invoicedate_sold !== '0000-00-00'">
                                    <span class="text-xs">Re.-Datum:</span> {{ sqlDateToFormattedDate(car.item_invoicedate_sold, 'dd.MM.yyyy') }}
                                  </div>
                                  <div
                                    v-if="car.item_opos_finished_at"
                                    class="text-sm mt-2 text-green-700"
                                  >
                                    OPOS abgeschlossen
                                  </div>
                                </td>
                                <td class="relative whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium align-top">
                                  <!-- eslint-disable vue/no-v-html -->
                                  <div
                                    v-if="car.item_dispatch_notes"
                                    v-html="nl2br(car.item_dispatch_notes)"
                                  />
                                  <!-- eslint-enable vue/no-v-html -->

                                  <mia-is-allowed
                                    to="customer_portal"
                                    who="dealer"
                                  >
                                    <div
                                      :class="{
                                        'mt-2': !!car.item_dispatch_notes
                                      }"
                                      class="space-y-2"
                                    >
                                      <div
                                        v-if="car.item_dispatch_source === 'customer'"
                                        class="text-green-600 font-bold"
                                      >
                                        Terminbuchung durch Kunde
                                      </div>
                                      <div
                                        v-if="car.item_registration_package === 'customer'"
                                        :class="{
                                          'text-red-600': !car.item_registration_certificate_uploaded_at,
                                          'text-green-600': !!car.item_registration_certificate_uploaded_at
                                        }"
                                      >
                                        Zulassungsbescheinigung Teil II {{ car.item_registration_certificate_uploaded_at ? 'hochgeladen' : 'nicht hochgeladen' }}
                                      </div>
                                    </div>
                                  </mia-is-allowed>
                                </td>
                              </tr>
                            </template>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </mia-wait>
      </div>
      <div v-show="activeTab === 'upcomingDispatches'">
        <mia-wait
          for="loading upcoming dispatch filters"
          class="mt-4"
        >
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <template #default>
            <div class="grid grid-cols-5 gap-6 p-4 -mx-2 bg-gray-200 items-start">
              <mia-feature-input
                v-model="filters.dispatchFrom"
                label="Terminierte Auslieferung von"
                type="date"
              />
              <mia-feature-input
                v-model="filters.dispatchTo"
                label="Terminierte Auslieferung bis"
                type="date"
              />
              <div
                class="relative flex flex-col justify-end"
              >
                <label
                  for="dispatchAssigneeSelect"
                  class="mb-1 mr-2 tracking-wider text-gray-800"
                >
                  Zuständig für die Auslieferung
                </label>

                <select
                  id="dispatchAssigneeSelect"
                  v-model="filters.dispatchAssignees"
                  class="form-select py-2 pl-3 bg-white bg-no-repeat border border-gray-500 rounded appearance-none"
                  :disabled="filters.dispatchAssigneeSelf"
                  multiple
                >
                  <option
                    v-for="dispatchAssignee in dispatchAssignees"
                    :key="dispatchAssignee.id"
                    class="text-black"
                    :value="dispatchAssignee.id"
                  >
                    {{ dispatchAssignee.item_full_name }}
                  </option>
                </select>
                <mia-multi-select-hint class="mt-1" />
                <label
                  class="font-normal flex items-center text-sm mt-2 cursor-pointer"
                  style="margin-bottom: 0"
                >
                  <mia-checkbox
                    v-model="filters.dispatchAssigneeSelf"
                    :checked="filters.dispatchAssigneeSelf"
                    class="w-5 h-5"
                    @input="$set(filters, 'dispatchAssigneeSelf', $event.target.checked)"
                  />
                  <span class="ml-2 block mt-px">Nur meine Auslieferungen anzeigen</span>
                </label>
              </div>
              <mia-feature-select
                v-model="filters.seller"
                label="Verkäufer"
                :options="sellers"
                :name-by="(seller) => `${seller.item_lastname}, ${seller.item_firstname}`"
                track-by="id"
              />
              <mia-feature-select
                v-model="filters.salesChannel"
                label="Vertriebskanal"
                :options="salesChannels"
                name-by="item_title"
                track-by="id"
              />
            </div>
          </template>
        </mia-wait>

        <mia-wait
          for="loading upcoming dispatches"
          class="mt-6"
        >
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <template #default>
            <div
              v-if="messages.length > 0"
              class="mt-6"
            >
              <mia-notice permanent>
                <div class="space-y-4">
                  <p
                    v-for="message in messages"
                    :key="message"
                    style="margin: 0"
                  >
                    {{ message }}
                  </p>
                </div>
              </mia-notice>
            </div>

            <div class="mt-6">
              <div class="flex flex-col">
                <div class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-sm">
                      <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-gray-400">
                          <tr>
                            <th
                              scope="col"
                              class="py-2 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                            >
                              Termin
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Terminierte Zulassung
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Zulassung durch
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Übergabeinspektion
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Aufbereitung
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Fahrzeug
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Verkäufer
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Kunde
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              Brieflagerplatz
                            </th>
                            <th
                              scope="col"
                              class="px-3 py-2 text-left text-sm font-semibold text-gray-900"
                            >
                              OPOS
                            </th>
                            <th
                              scope="col"
                              class="relative py-2 pl-3 pr-4"
                            >
                              Anmerkungen
                            </th>
                          </tr>
                        </thead>
                        <tbody class="bg-white">
                          <template v-if="cars.length === 0">
                            <tr>
                              <td
                                colspan="12"
                                class="whitespace-nowrap p-4 text-sm font-medium text-gray-900 text-center"
                              >
                                Keine anstehenden Auslieferungen gefunden.
                              </td>
                            </tr>
                          </template>
                          <template v-else>
                            <template v-for="(carsInCalendarWeek, calendarWeek) in carsByCalendarWeek">
                              <template v-if="calendarWeek === currentCalendarWeek">
                                <tr
                                  v-if="hasEntriesBeforeCalendarWeek(calendarWeek)"
                                  :key="`${calendarWeek}-before-indicator`"
                                  class="bg-gray-400"
                                >
                                  <td
                                    colspan="12"
                                    class="p-3"
                                  >
                                    <div class="text-gray-900 flex flex-col justify-center items-center">
                                      <div class="flex pb-2">
                                        <svg
                                          class="mr-1"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        >
                                          <polyline points="14 9 9 4 4 9" />
                                          <path d="M20 20h-7a4 4 0 0 1-4-4V4" />
                                        </svg>
                                        <span>verstrichene Termine</span>
                                      </div>
                                      <div
                                        class="border-t border-orange-mia mx-2 w-full"
                                        style="max-width: 40%;"
                                      />
                                      <div class="flex pt-2">
                                        <span>anstehende Termine</span>
                                        <svg
                                          class="ml-1 mt-px"
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          stroke-width="2"
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                        >
                                          <polyline points="10 15 15 20 20 15" />
                                          <path d="M4 4h7a4 4 0 0 1 4 4v12" />
                                        </svg>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              </template>
                              <template v-if="carsInCalendarWeek.length > 0">
                                <tr
                                  :key="calendarWeek"
                                  class="bg-gray-300"
                                >
                                  <td
                                    colspan="12"
                                    class="py-5 px-4"
                                  >
                                    <h2
                                      style="margin: 0; padding: 0"
                                      :class="{
                                        'font-bold flex items-center': calendarWeek === currentCalendarWeek
                                      }"
                                    >
                                      {{ formatCalendarWeek(calendarWeek) }}<span
                                        v-if="calendarWeek === currentCalendarWeek"
                                        class="font-normal ml-2 text-base tracking-wide"
                                      >
                                        - Diese Woche
                                      </span>
                                    </h2>
                                  </td>
                                </tr>
                                <template v-for="(car, index) in carsInCalendarWeek">
                                  <tr
                                    :key="`${car.id}-car`"
                                    class="hover:bg-blue-100"
                                    :class="{
                                      'bg-gray-100': index % 2 === 0
                                    }"
                                  >
                                    <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 align-top">
                                      {{ timestampToFormattedDateTime(car.item_dispatch) }}
                                      <div
                                        v-if="car.item_terminated_ak_date"
                                        class="text-sm mt-2 text-red-600"
                                      >
                                        Terminierte AK Meldung am <b>{{ sqlDateToFormattedDate(car.item_terminated_ak_date, 'dd.MM.yyyy') }}</b>
                                      </div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ sqlDateToFormattedDate(car.item_registration_date) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ getRegistrationInstanceLabel(car.item_registration_instance) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ sqlDateToFormattedDate(car.item_handover_inspection_date) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      {{ sqlDateToFormattedDate(car.item_preparation_date) }}
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <div
                                        :style="{
                                          color: `#${car.item_status_color}`
                                        }"
                                      >
                                        {{ car.item_status_title }}
                                      </div>
                                      <div>{{ car.item_type }}</div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <div>
                                        <router-link
                                          :to="`/cars/${car.id}`"
                                          class="hover:underline"
                                        >
                                          {{ car.item_fin || 'Ohne FIN' }}
                                        </router-link>
                                      </div>
                                      <div>
                                        {{ car.item_model_title }}
                                      </div>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <router-link
                                        :to="`/users/profile/${car.item_sold_seller}`"
                                        class="hover:underline"
                                      >
                                        {{ car.item_sold_seller_name }}
                                      </router-link>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <router-link
                                        :to="`/customers/${car.item_customer}`"
                                        class="hover:underline flex items-center"
                                      >
                                        <mia-streamline-icon
                                          :src="car.item_customer_company && car.item_customer_company.trim().length > 0 ? 'building-1' : 'single-neutral'"
                                          class="w-4 h-4 mr-1"
                                        />
                                        <span>{{ car.item_customer_company && car.item_customer_company.trim().length > 0 ? car.item_customer_company : car.item_customer_name }}</span>
                                      </router-link>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <router-link
                                        v-if="car.item_certificate_slot"
                                        :to="`/legacy/certificate_edit/${car.item_certificate_slot}`"
                                        class="hover:underline"
                                      >
                                        {{ car.item_certificate_slot_title }}
                                      </router-link>
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                      <span
                                        :class="{
                                          'text-red-600': car.item_opos_difference > 0,
                                          'text-green-600': car.item_opos_difference <= 0
                                        }"
                                      >
                                        {{ car.item_opos_difference > 0 ? '-' : '' }}{{ car.item_opos_difference | currency }}
                                      </span>
                                      <div v-if="car.item_invoicedate_sold && car.item_invoicedate_sold !== '0000-00-00'">
                                        <span class="text-xs">Re.-Datum:</span> {{ sqlDateToFormattedDate(car.item_invoicedate_sold, 'dd.MM.yyyy') }}
                                      </div>
                                      <div
                                        v-if="car.item_opos_finished_at"
                                        class="text-sm mt-2 text-green-700"
                                      >
                                        OPOS abgeschlossen
                                      </div>
                                      <div
                                        v-else
                                        class="text-sm mt-2 text-red-700"
                                      >
                                        OPOS noch nicht abgeschlossen
                                      </div>
                                    </td>
                                    <td class="relative whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium align-top">
                                      <!-- eslint-disable vue/no-v-html -->
                                      <div
                                        v-if="car.item_dispatch_notes"
                                        v-html="nl2br(car.item_dispatch_notes)"
                                      />
                                      <!-- eslint-enable vue/no-v-html -->

                                      <mia-is-allowed
                                        to="customer_portal"
                                        who="dealer"
                                      >
                                        <div
                                          :class="{
                                            'mt-2': !!car.item_dispatch_notes
                                          }"
                                          class="space-y-2"
                                        >
                                          <div
                                            v-if="car.item_dispatch_source === 'customer'"
                                            class="text-green-600 font-bold"
                                          >
                                            Terminbuchung durch Kunde
                                          </div>
                                          <div
                                            v-if="car.item_registration_package === 'customer'"
                                            :class="{
                                              'text-red-600': !car.item_registration_certificate_uploaded_at,
                                              'text-green-600': !!car.item_registration_certificate_uploaded_at
                                            }"
                                          >
                                            Zulassungsbescheinigung Teil II {{ car.item_registration_certificate_uploaded_at ? 'hochgeladen' : 'nicht hochgeladen' }}
                                          </div>
                                        </div>
                                      </mia-is-allowed>
                                    </td>
                                  </tr>
                                  <tr
                                    :key="`${car.id}-exchange`"
                                    class="hover:bg-blue-100"
                                    :class="{
                                      'bg-gray-100': index % 2 === 0
                                    }"
                                  >
                                    <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900">
                                      <mia-streamline-icon
                                        v-tooltip="'Inzahlungnahme'"
                                        src="arrow-button-up"
                                        class="w-5 h-5"
                                        title="Inzahlungnahme"
                                      />
                                    </td>
                                    <td
                                      colspan="4"
                                      class="whitespace-nowrap px-3 py-3 text-sm text-gray-900"
                                    />
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 font-bold">
                                      Inzahlungnahme
                                    </td>
                                    <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900">
                                      <router-link
                                        :to="`/cars/${car.item_exchange_id}`"
                                        class="hover:underline"
                                      >
                                        {{ car.item_exchange_fin || 'Ohne FIN' }}
                                      </router-link>
                                    </td>
                                    <td
                                      colspan="4"
                                      class="whitespace-nowrap px-3 py-3 text-sm text-gray-900"
                                    />
                                    <td class="relative whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium" />
                                  </tr>
                                </template>
                              </template>
                            </template>
                          </template>
                          <template v-if="hasCarsWithoutDispatchDate">
                            <template v-for="(carsInYearAndMonth, yearAndMonth) in orderedCarsWithoutDispatchDate">
                              <tr
                                :key="`${yearAndMonth}-indicator`"
                                class="bg-gray-400"
                              >
                                <td
                                  colspan="12"
                                  class="p-3"
                                >
                                  <div class="text-gray-900 flex justify-center items-center">
                                    <div
                                      class="border-t border-orange-mia mx-2 w-full"
                                      style="max-width: 20%;"
                                    />
                                    <div>
                                      {{ carsInYearAndMonth.length }} Fahrzeug{{ carsInYearAndMonth.length === 1 ? '' : 'e' }} im Monat {{ formatYearAndMonth(yearAndMonth) }} ohne Auslieferungstermin
                                    </div>
                                    <div
                                      class="border-t border-orange-mia mx-2 w-full"
                                      style="max-width: 20%;"
                                    />
                                  </div>
                                </td>
                              </tr>
                              <tr
                                v-for="(car, index) in carsInYearAndMonth"
                                :key="`${car.id}-car-without-dispatch-date`"
                                class="hover:bg-blue-100"
                                :class="{
                                  'bg-gray-100': index % 2 === 0
                                }"
                              >
                                <td class="whitespace-nowrap py-3 pl-4 pr-3 text-sm font-medium text-gray-900 align-top" />
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ sqlDateToFormattedDate(car.item_registration_date) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ getRegistrationInstanceLabel(car.item_registration_instance) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ sqlDateToFormattedDate(car.item_handover_inspection_date) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  {{ sqlDateToFormattedDate(car.item_preparation_date) }}
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <div
                                    :style="{
                                      color: `#${car.item_status_color}`
                                    }"
                                  >
                                    {{ car.item_status_title }}
                                  </div>
                                  <div>{{ car.item_type }}</div>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <div>
                                    <router-link
                                      :to="`/cars/${car.id}`"
                                      class="hover:underline"
                                    >
                                      {{ car.item_fin || 'Ohne FIN' }}
                                    </router-link>
                                  </div>
                                  <div>
                                    {{ car.item_model_title }}
                                  </div>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <router-link
                                    :to="`/users/profile/${car.item_sold_seller}`"
                                    class="hover:underline"
                                  >
                                    {{ car.item_sold_seller_name }}
                                  </router-link>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <router-link
                                    :to="`/customers/${car.item_customer}`"
                                    class="hover:underline flex items-center"
                                  >
                                    <mia-streamline-icon
                                      :src="car.item_customer_company && car.item_customer_company.trim().length > 0 ? 'building-1' : 'single-neutral'"
                                      class="w-4 h-4 mr-1"
                                    />
                                    <span>{{ car.item_customer_company && car.item_customer_company.trim().length > 0 ? car.item_customer_company : car.item_customer_name }}</span>
                                  </router-link>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <router-link
                                    v-if="car.item_certificate_slot"
                                    :to="`/legacy/certificate_edit/${car.item_certificate_slot}`"
                                    class="hover:underline"
                                  >
                                    {{ car.item_certificate_slot_title }}
                                  </router-link>
                                </td>
                                <td class="whitespace-nowrap px-3 py-3 text-sm text-gray-900 align-top">
                                  <span
                                    :class="{
                                      'text-red-600': car.item_opos_difference > 0,
                                      'text-green-600': car.item_opos_difference <= 0
                                    }"
                                  >
                                    {{ car.item_opos_difference > 0 ? '-' : '' }}{{ car.item_opos_difference | currency }}
                                  </span>
                                  <div v-if="car.item_invoicedate_sold && car.item_invoicedate_sold !== '0000-00-00'">
                                    <span class="text-xs">Re.-Datum:</span> {{ sqlDateToFormattedDate(car.item_invoicedate_sold, 'dd.MM.yyyy') }}
                                  </div>
                                  <div
                                    v-if="car.item_opos_finished_at"
                                    class="text-sm mt-2 text-green-700"
                                  >
                                    OPOS abgeschlossen
                                  </div>
                                </td>
                                <td class="relative whitespace-nowrap py-3 pl-3 pr-4 text-sm font-medium align-top">
                                  <!-- eslint-disable vue/no-v-html -->
                                  <div
                                    v-if="car.item_dispatch_notes"
                                    v-html="nl2br(car.item_dispatch_notes)"
                                  />
                                  <!-- eslint-enable vue/no-v-html -->

                                  <mia-is-allowed
                                    to="customer_portal"
                                    who="dealer"
                                  >
                                    <div
                                      :class="{
                                        'mt-2': !!car.item_dispatch_notes
                                      }"
                                      class="space-y-2"
                                    >
                                      <div
                                        v-if="car.item_dispatch_source === 'customer'"
                                        class="text-green-600 font-bold"
                                      >
                                        Terminbuchung durch Kunde
                                      </div>
                                      <div
                                        v-if="car.item_registration_package === 'customer'"
                                        :class="{
                                          'text-red-600': !car.item_registration_certificate_uploaded_at,
                                          'text-green-600': !!car.item_registration_certificate_uploaded_at
                                        }"
                                      >
                                        Zulassungsbescheinigung Teil II {{ car.item_registration_certificate_uploaded_at ? 'hochgeladen' : 'nicht hochgeladen' }}
                                      </div>
                                    </div>
                                  </mia-is-allowed>
                                </td>
                              </tr>
                            </template>
                          </template>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </mia-wait>
      </div>
      <div v-show="activeTab === 'terminatedPotentials'">
        <mia-terminated-potentials />
      </div>
    </div>
  </div>
</template>
